/**
 * Program Management
 * Firebase CRUD operations for workout programs
 *  REFACTORED: Program selection now uses Dexie (IndexedDB) as single source of truth
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { db as dexieDb } from './db';
import { WorkoutProgram, ProgramMetadata, ProgramLevel, WorkoutDay, UserProgramSelection } from './types/program';
import { mergeProgramSelection } from './dexieFirebaseSync';

/**
 * Convert Firestore Timestamp to Date, or return Date if already a Date
 */
function toDate(value: any): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value.toDate && typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

/**
 * Remove undefined values from an object (recursively)
 * Firestore doesn't allow undefined values
 */
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item)).filter(item => item !== undefined && item !== null);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip undefined, null, and empty strings
      if (value !== undefined && value !== null && value !== '') {
        const cleanedValue = removeUndefined(value);
        // Only add if the cleaned value is not undefined or empty
        if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
          cleaned[key] = cleanedValue;
        }
      }
    }
    return cleaned;
  }
  
  return obj;
}

/**
 * Save program to Firebase
 * Only admins should be able to call this
 */
export async function saveProgramToFirebase(program: WorkoutProgram): Promise<void> {
  try {
    const programRef = doc(db, 'programs', program.metadata.id);
    
    // Convert dates to Firestore Timestamps
    const programData = {
      metadata: {
        ...program.metadata,
        createdAt: Timestamp.fromDate(program.metadata.createdAt),
        updatedAt: Timestamp.fromDate(program.metadata.updatedAt),
      },
      workouts: program.workouts,
      // IMPORTANT: Also save templates for programs that use template-based system
      templates: program.templates || null,
      periodization: program.periodization || null,
    };

    // Remove any undefined values (Firestore doesn't allow them)
    const cleanedData = removeUndefined(programData);

    await setDoc(programRef, cleanedData);
    console.log(` Program saved: ${program.metadata.name} (${program.metadata.id})`);
  } catch (error) {
    console.error(' Error saving program:', error);
    throw error;
  }
}

/**
 * Get program by ID
 */
export async function getProgramById(programId: string): Promise<WorkoutProgram | null> {
  try {
    const programRef = doc(db, 'programs', programId);
    const programSnap = await getDoc(programRef);

    if (!programSnap.exists()) {
      console.log(`Program ${programId} not found`);
      return null;
    }

    const data = programSnap.data();
    
    // Debug logging
    console.log('� Program data from Firestore:', {
      hasMetadata: !!data.metadata,
      hasPeriodization: !!data.periodization,
      hasTemplates: !!data.templates,
      templateKeys: data.templates ? Object.keys(data.templates) : 'NONE',
      hasWorkouts: !!data.workouts,
    });
    
    return {
      metadata: {
        ...data.metadata,
        createdAt: toDate(data.metadata.createdAt),
        updatedAt: toDate(data.metadata.updatedAt),
      },
      periodization: data.periodization,
      templates: data.templates,
      workouts: data.workouts || [],
    };
  } catch (error) {
    console.error(' Error fetching program:', error);
    throw error;
  }
}

/**
 * Get all programs for a specific level
 */
export async function getProgramsByLevel(level: ProgramLevel): Promise<WorkoutProgram[]> {
  try {
    const q = query(
      collection(db, 'programs'),
      where('metadata.level', '==', level),
      where('metadata.isActive', '==', true),
      orderBy('metadata.createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        metadata: {
          ...data.metadata,
          createdAt: toDate(data.metadata.createdAt),
          updatedAt: toDate(data.metadata.updatedAt),
        },
        periodization: data.periodization,
        templates: data.templates,
        workouts: data.workouts || [],
      };
    });
  } catch (error) {
    console.error(' Error fetching programs by level:', error);
    throw error;
  }
}

/**
 * Get all active programs
 * Merges Firestore programs with static program definitions.
 * Firestore versions take priority; static programs fill in any gaps.
 */
export async function getAllActivePrograms(): Promise<WorkoutProgram[]> {
  try {
    const q = query(
      collection(db, 'programs'),
      where('metadata.isActive', '==', true),
      orderBy('metadata.level'),
      orderBy('metadata.createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const firestorePrograms: WorkoutProgram[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        metadata: {
          ...data.metadata,
          createdAt: toDate(data.metadata.createdAt),
          updatedAt: toDate(data.metadata.updatedAt),
        },
        periodization: data.periodization,
        templates: data.templates,
        workouts: data.workouts || [],
      };
    });

    // Merge with static programs — add any that aren't already in Firestore
    const { STATIC_PROGRAMS } = await import('./programs/index');
    const firestoreIds = new Set(firestorePrograms.map(p => p.metadata.id));

    const staticExtras = Object.values(STATIC_PROGRAMS)
      .filter(p => !firestoreIds.has(p.metadata.id) && p.metadata.isActive !== false);

    // Deduplicate static extras (some programs have legacy aliases pointing to the same object)
    const seenStaticIds = new Set<string>();
    const uniqueStaticExtras = staticExtras.filter(p => {
      if (seenStaticIds.has(p.metadata.id)) return false;
      seenStaticIds.add(p.metadata.id);
      return true;
    });

    return [...firestorePrograms, ...uniqueStaticExtras];
  } catch (error) {
    console.error(' Error fetching all programs:', error);
    throw error;
  }
}

/**
 * Get workout for specific week and day
 */
export function getWorkoutByWeekDay(
  program: WorkoutProgram,
  week: number,
  day: number
): WorkoutDay | null {
  return program.workouts.find(w => w.week === week && w.day === day) || null;
}

/**
 * Delete program
 * Only admins should be able to call this
 */
export async function deleteProgramFromFirebase(programId: string): Promise<void> {
  try {
    const programRef = doc(db, 'programs', programId);
    await deleteDoc(programRef);
    console.log(` Program deleted: ${programId}`);
  } catch (error) {
    console.error(' Error deleting program:', error);
    throw error;
  }
}

/**
 * Soft delete (mark as inactive)
 */
export async function deactivateProgram(programId: string): Promise<void> {
  try {
    const programRef = doc(db, 'programs', programId);
    await setDoc(programRef, {
      'metadata.isActive': false,
      'metadata.updatedAt': Timestamp.now(),
    }, { merge: true });
    console.log(` Program deactivated: ${programId}`);
  } catch (error) {
    console.error(' Error deactivating program:', error);
    throw error;
  }
}

/**
 * Bound a Firestore round-trip so an unreachable backend cannot hang the UI.
 *
 * Every day-tracking mutation awaits its Firestore write, but the state is
 * already durable in Dexie by the time that starts — so offline (or a stalled
 * WKWebView connection) left spinners running and modals open forever with the
 * work already safely saved locally. The read paths in this codebase already
 * had 8-10s timeouts; the writes never got them.
 *
 * Resolves to `null` on timeout rather than throwing: the local write
 * succeeded, and the sync layer reconciles with the server later. A timeout is
 * not a failure the user needs to act on.
 */
async function withFirestoreTimeout<T>(
  work: Promise<T>,
  label: string,
  ms = 8000
): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<null>(resolve => {
        timer = setTimeout(() => {
          console.warn(`[programManagement] ${label} timed out after ${ms}ms — saved locally, will sync later`);
          resolve(null);
        }, ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Save user's program selection
 */
export async function saveUserProgramSelection(
  userId: string,
  selection: UserProgramSelection
): Promise<void> {
  try {
    console.log('� Saving program selection to Dexie...', { userId, selection });
    
    //  Get existing profile to preserve syncedAt and other fields
    const existing = await dexieDb.userProfiles.get(userId);
    console.log('� Existing Dexie profile:', existing);
    
    //  PRIMARY: Save to Dexie (IndexedDB) - single source of truth
    // CRITICAL: Preserve existing syncedAt and other fields!
    //  FIX: Now includes completedDays and skippedDays!
    const dexieData: any = {
      userId,
      programSelection: {
        programId: selection.programId,
        programLevel: selection.programLevel,
        startDate: selection.startDate.toISOString(),
        currentWeek: selection.currentWeek,
        currentDay: selection.currentDay,
        completedDays: selection.completedDays || [],
        skippedDays: selection.skippedDays || [],
        // Un-skip tombstones and the explicit-position stamp are part of the
        // persisted shape. This whitelist silently dropping them is what made
        // both features dead code: the flag was written in memory, never
        // stored, and read back as undefined forever after.
        ...((selection as any).unskippedDays?.length
          ? { unskippedDays: (selection as any).unskippedDays }
          : {}),
        ...((selection as any).positionSetAt
          ? { positionSetAt: (selection as any).positionSetAt }
          : {}),
        ...((selection as any).positionWeek != null
          ? { positionWeek: (selection as any).positionWeek }
          : {}),
        ...((selection as any).positionDay != null
          ? { positionDay: (selection as any).positionDay }
          : {}),
        ...(selection.originalStartDate
          ? { originalStartDate: selection.originalStartDate }
          : {}),
        ...(selection.restartedAt ? { restartedAt: selection.restartedAt } : {}),
        // updatedAt/syncVersion are what the merge uses to rank the two sides.
        // Omitting them here left the Dexie record permanently stamped '' / 0,
        // which mis-fires the restart branch for anyone who has ever restarted.
        syncVersion: (selection as any).syncVersion || 0,
        updatedAt: new Date().toISOString(),
      },
      updatedAt: new Date(),
    };
    
    // Preserve existing fields
    if (existing?.syncedAt) dexieData.syncedAt = existing.syncedAt;
    if (existing?.displayName) dexieData.displayName = existing.displayName;
    if (existing?.email) dexieData.email = existing.email;
    if (existing?.preferences) dexieData.preferences = existing.preferences;
    
    console.log('� Dexie data to save:', dexieData);
    await dexieDb.userProfiles.put(dexieData);
    
    // Verify it was saved
    const saved = await dexieDb.userProfiles.get(userId);
    console.log(' Program selection saved to Dexie:', saved);
    
    //  BACKUP: Also save to Firestore for cross-device sync
    // Step 6: Read current Firestore state and merge before writing,
    // to prevent Device A from clobbering Device B's more-advanced state.
    const userRef = doc(db, 'users', userId);
    try {
      const currentSnap = await withFirestoreTimeout(getDoc(userRef), 'read-before-merge');
      // Read timed out: bail out of the Firestore leg entirely. Dexie already
      // has the write, and dexieFirebaseSync will merge properly once the
      // connection is back. Do NOT fall through to a direct write — treating
      // "couldn't reach the server" as "no remote data" would let this device
      // overwrite a more advanced device's position.
      if (!currentSnap) return;
      const remoteData = currentSnap.exists() ? currentSnap.data()?.programSelection : null;

      if (remoteData) {
        const remoteStartDate = remoteData.startDate?.toDate
          ? remoteData.startDate.toDate().toISOString()
          : (typeof remoteData.startDate === 'string' ? remoteData.startDate : new Date(remoteData.startDate).toISOString());

        const localForMerge = {
          programId: selection.programId,
          programLevel: selection.programLevel,
          startDate: selection.startDate.toISOString(),
          currentWeek: selection.currentWeek,
          currentDay: selection.currentDay,
          completedDays: selection.completedDays || [],
          skippedDays: selection.skippedDays || [],
          ...((selection as any).unskippedDays?.length
            ? { unskippedDays: (selection as any).unskippedDays }
            : {}),
          // Carried into the merge so an explicit position choice can beat the
          // never-regress rule. Without it every backward move loses here.
          ...((selection as any).positionSetAt
            ? { positionSetAt: (selection as any).positionSetAt }
            : {}),
          ...((selection as any).positionWeek != null
            ? { positionWeek: (selection as any).positionWeek }
            : {}),
          ...((selection as any).positionDay != null
            ? { positionDay: (selection as any).positionDay }
            : {}),
          ...(selection.originalStartDate
            ? { originalStartDate: selection.originalStartDate }
            : {}),
          syncVersion: (dexieData.programSelection as any)?.syncVersion || 0,
          updatedAt: new Date().toISOString(),
          ...(selection.restartedAt ? { restartedAt: selection.restartedAt } : {}),
        };
        const remoteForMerge = {
          programId: remoteData.programId,
          programLevel: remoteData.programLevel,
          startDate: remoteStartDate,
          currentWeek: remoteData.currentWeek,
          currentDay: remoteData.currentDay,
          completedDays: remoteData.completedDays || [],
          skippedDays: remoteData.skippedDays || [],
          ...(remoteData.unskippedDays?.length ? { unskippedDays: remoteData.unskippedDays } : {}),
          ...(remoteData.positionSetAt ? { positionSetAt: remoteData.positionSetAt } : {}),
          ...(remoteData.restartedAt ? { restartedAt: remoteData.restartedAt } : {}),
          syncVersion: remoteData.syncVersion || 0,
          updatedAt: remoteData.updatedAt || '',
        };

        const merged = mergeProgramSelection(localForMerge, remoteForMerge);
        if (merged) {
          const firestoreSelection = {
            ...merged,
            startDate: Timestamp.fromDate(new Date(merged.startDate)),
          };
          await withFirestoreTimeout(
            setDoc(userRef, { programSelection: firestoreSelection }, { merge: true }),
            'merged write'
          );

          // Also update local Dexie if merge produced a different result
          if (merged.currentWeek !== selection.currentWeek || merged.currentDay !== selection.currentDay) {
            await dexieDb.userProfiles.update(userId, {
              programSelection: merged,
              updatedAt: new Date(),
            });
          }
        }
      } else {
        // No remote data — just write
        await withFirestoreTimeout(
          setDoc(userRef, {
            programSelection: {
              ...selection,
              startDate: Timestamp.fromDate(selection.startDate),
              updatedAt: new Date().toISOString(),
            },
          }, { merge: true }),
          'first write'
        );
      }
    } catch (mergeErr) {
      // If merge fails, still write to Firestore (best effort)
      console.warn('Program selection merge-before-write failed, writing directly:', mergeErr);
      await withFirestoreTimeout(
        setDoc(userRef, {
          programSelection: {
            ...selection,
            startDate: Timestamp.fromDate(selection.startDate),
          },
        }, { merge: true }),
        'fallback write'
      );
    }
    console.log(` Program selection synced to Firestore: ${selection.programId}`);

    // Track analytics
    import('@/lib/analytics').then(({ trackEvent }) => {
      trackEvent('program_selected', {
        program_id: selection.programId,
        program_level: selection.programLevel,
      });
    }).catch(() => {});
  } catch (error) {
    console.error(' Error saving user program selection:', error);
    throw error;
  }
}

/**
 * Get user's program selection
 */
export async function getUserProgramSelection(userId: string): Promise<UserProgramSelection | null> {
  try {
    console.log('� Reading program selection from Dexie for userId:', userId);
    
    //  PRIMARY: Read from Dexie (IndexedDB) - single source of truth
    const userProfile = await dexieDb.userProfiles.get(userId);
    console.log('� Dexie userProfile:', userProfile);
    
    if (userProfile?.programSelection) {
      console.log(' Found program selection in Dexie:', userProfile.programSelection);
      //  FIX: Now loads completedDays and skippedDays from Dexie!
      const result: any = {
        programId: userProfile.programSelection.programId,
        programLevel: (userProfile.programSelection.programLevel || 'intermediate') as any,
        startDate: new Date(userProfile.programSelection.startDate),
        currentWeek: userProfile.programSelection.currentWeek,
        currentDay: userProfile.programSelection.currentDay,
        completedDays: userProfile.programSelection.completedDays || [],
        skippedDays: userProfile.programSelection.skippedDays || [],
      };
      const ps = userProfile.programSelection as any;
      if (ps.restartedAt) result.restartedAt = ps.restartedAt;
      // Read back everything the write path stores. A field that is written but
      // not read is the same as a field that was never written — that asymmetry
      // is exactly how the manual-navigation guard stayed dead in production.
      if (ps.unskippedDays?.length) result.unskippedDays = ps.unskippedDays;
      if (ps.positionSetAt) result.positionSetAt = ps.positionSetAt;
      if (ps.originalStartDate) result.originalStartDate = ps.originalStartDate;
      if (ps.syncVersion != null) result.syncVersion = ps.syncVersion;
      if (ps.updatedAt) result.updatedAt = ps.updatedAt;
      console.log(` Loaded ${result.completedDays.length} completed days, ${result.skippedDays.length} skipped days from Dexie`);
      return result;
    }
    
    //  FALLBACK: Try Firestore if not in Dexie (for cross-device sync)
    console.log(' Program selection not in Dexie - checking Firestore');
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists() && userSnap.data().programSelection) {
      const data = userSnap.data();
      //  FIX: Ensure completedDays and skippedDays are loaded from Firestore!
      const selection: UserProgramSelection = {
        programId: data.programSelection.programId,
        programLevel: data.programSelection.programLevel || 'intermediate',
        startDate: data.programSelection.startDate?.toDate?.() || new Date(data.programSelection.startDate),
        currentWeek: data.programSelection.currentWeek,
        currentDay: data.programSelection.currentDay,
        completedDays: data.programSelection.completedDays || [],
        skippedDays: data.programSelection.skippedDays || [],
        ...(data.programSelection.restartedAt ? { restartedAt: data.programSelection.restartedAt } : {}),
        // Mirror of the Dexie read above — same fields, same reason.
        ...(data.programSelection.unskippedDays?.length
          ? { unskippedDays: data.programSelection.unskippedDays }
          : {}),
        ...(data.programSelection.positionSetAt
          ? { positionSetAt: data.programSelection.positionSetAt }
          : {}),
        ...(data.programSelection.originalStartDate
          ? { originalStartDate: data.programSelection.originalStartDate }
          : {}),
        ...(data.programSelection.syncVersion != null
          ? { syncVersion: data.programSelection.syncVersion }
          : {}),
        ...(data.programSelection.updatedAt
          ? { updatedAt: data.programSelection.updatedAt }
          : {}),
      } as any;
      
      console.log('� Caching program selection from Firestore to Dexie:', selection);
      console.log('� completedDays from Firestore:', selection.completedDays);
      console.log('� skippedDays from Firestore:', selection.skippedDays);
      
      //  Get existing profile to preserve syncedAt and other fields
      const existingProfile = await dexieDb.userProfiles.get(userId);
      console.log('� Existing profile before cache:', existingProfile);
      
      // Cache to Dexie for next time - PRESERVE existing fields!
      //  FIX: Now includes completedDays and skippedDays!
      const dexieData: any = {
        userId,
        programSelection: {
          programId: selection.programId,
          programLevel: selection.programLevel,
          startDate: selection.startDate.toISOString(),
          currentWeek: selection.currentWeek,
          currentDay: selection.currentDay,
          completedDays: selection.completedDays,
          skippedDays: selection.skippedDays,
        },
        updatedAt: new Date(),
      };
      
      // Preserve existing fields
      if (existingProfile?.syncedAt) dexieData.syncedAt = existingProfile.syncedAt;
      if (existingProfile?.displayName) dexieData.displayName = existingProfile.displayName;
      if (existingProfile?.email) dexieData.email = existingProfile.email;
      if (existingProfile?.preferences) dexieData.preferences = existingProfile.preferences;
      
      await dexieDb.userProfiles.put(dexieData);
      
      // Verify it was saved
      const verified = await dexieDb.userProfiles.get(userId);
      console.log(' Cached program selection from Firestore to Dexie. Verified:', verified);
      
      return selection;
    }

    console.log(' No program selection found in Dexie or Firestore');
    return null;
  } catch (error) {
    console.error(' Error fetching user program selection:', error);
    return null;
  }
}

/**
 * Get user's current workout
 * Combines program + user progress
 */
export async function getUserCurrentWorkout(userId: string): Promise<{
  program: WorkoutProgram;
  workout: WorkoutDay;
  selection: UserProgramSelection;
} | null> {
  try {
    // Get user's program selection
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      console.log('User has no program selected');
      return null;
    }

    // Get the program
    const program = await getProgramById(selection.programId);
    if (!program) {
      console.log(`Program ${selection.programId} not found`);
      return null;
    }

    // Get current workout
    const workout = getWorkoutByWeekDay(program, selection.currentWeek, selection.currentDay);
    if (!workout) {
      console.log(`Workout not found for Week ${selection.currentWeek} Day ${selection.currentDay}`);
      return null;
    }

    return { program, workout, selection };
  } catch (error) {
    console.error(' Error fetching user current workout:', error);
    throw error;
  }
}

/**
 * Update user progress (mark day complete)
 */
export async function markDayComplete(
  userId: string,
  week: number,
  day: number
): Promise<void> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }

    // Add to completed days if not already there
    const completedKey = `${week}-${day}`;
    const alreadyCompleted = selection.completedDays.some(
      d => `${d.week}-${d.day}` === completedKey
    );

    if (!alreadyCompleted) {
      selection.completedDays.push({ week, day });
    }

    // Remove from skipped if it was there
    selection.skippedDays = selection.skippedDays.filter(
      d => `${d.week}-${d.day}` !== completedKey
    );

    // Calculate next day
    const nextDay = calculateNextDay(selection);
    selection.currentWeek = nextDay.week;
    selection.currentDay = nextDay.day;

    await saveUserProgramSelection(userId, selection);
  } catch (error) {
    console.error(' Error marking day complete:', error);
    throw error;
  }
}

/**
 * Calculate next day based on completed/skipped days
 */
function calculateNextDay(selection: UserProgramSelection): { week: number; day: number } {
  const program = selection.programId; // Would need to fetch program for max days
  let { week, day } = { week: selection.currentWeek, day: selection.currentDay };

  // Simple increment (can be enhanced with program-aware logic)
  day++;
  if (day > 7) {
    day = 1;
    week++;
  }

  // Skip already completed or skipped days
  const isCompleted = (w: number, d: number) =>
    selection.completedDays.some(cd => cd.week === w && cd.day === d);
  const isSkipped = (w: number, d: number) =>
    selection.skippedDays.some(sd => sd.week === w && sd.day === d);

  while (isCompleted(week, day) || isSkipped(week, day)) {
    day++;
    if (day > 7) {
      day = 1;
      week++;
    }
    // Prevent infinite loop
    if (week > 13) break;
  }

  return { week, day };
}

